import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useLang, useT, useLocalePath } from "@/i18n";
import { useSeo, absoluteUrl } from "@/lib/seo";
import { Calendar, Clock, MapPin, Euro, Users, CheckCircle2, UtensilsCrossed, MessageCircleQuestion } from "lucide-react";
import { SpeakerList } from "@/components/SpeakerList";
import { EventRow, evTitle, evDesc, fmtDate, fmtTime, fmtPrice, isAnnouncement, evDateNote, cateringLabel } from "@/lib/events";

const schema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(50),
  note: z.string().trim().max(2000),
});

const VeranstaltungDetail = () => {
  const { slug } = useParams();
  const lang = useLang();
  const lp = useLocalePath();
  const [ev, setEv] = useState<EventRow | null | undefined>(undefined);
  const [taken, setTaken] = useState(0);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", note: "", website: "" });
  const [persons, setPersons] = useState(1);
  const [cPriv, setCPriv] = useState(false);
  const [cBind, setCBind] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const t = useT(
    { back: "← Alle Veranstaltungen", reg: "Jetzt anmelden", full: "Ausgebucht", past: "Diese Veranstaltung hat bereits stattgefunden.", seats: "freie Plätze", fn: "Vorname *", ln: "Nachname *", em: "E-Mail *", ph: "Telefon (optional)", pe: "Anzahl Personen", no: "Anmerkung (optional)", priv1: "Ich habe die ", privLink: "Datenschutzerklärung", priv2: " (im Seitenfuß) gelesen und stimme der Verarbeitung meiner Daten zur Anmeldung zu. *", bind: (p: string) => `Ich melde mich verbindlich an. Der Teilnahmebeitrag von ${p} pro Person ist zu entrichten. *`, pay: "Informationen zur Bezahlung erhalten Sie per E-Mail.", invalid: "Bitte füllen Sie alle Pflichtfelder korrekt aus.", fail: "Die Anmeldung konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder schreiben Sie an office@ibpraxis.net.", fullErr: "Leider sind nicht mehr genügend Plätze frei.", ok: "Vielen Dank für Ihre Anmeldung!", okText: "Sie erhalten in Kürze eine Bestätigung per E-Mail.", notFound: "Veranstaltung nicht gefunden.", formH: "Anmeldung", dur: "Min." },
    { back: "← All events", reg: "Register now", full: "Fully booked", past: "This event has already taken place.", seats: "seats left", fn: "First name *", ln: "Last name *", em: "Email *", ph: "Phone (optional)", pe: "Number of people", no: "Note (optional)", priv1: "I have read the ", privLink: "privacy policy", priv2: " (in the footer) and agree to my data being processed for this registration. *", bind: (p: string) => `I am registering bindingly. The participation fee of ${p} per person is payable. *`, pay: "Payment details will be sent to you by email.", invalid: "Please fill in all required fields correctly.", fail: "Registration could not be sent. Please try again or write to office@ibpraxis.net.", fullErr: "Unfortunately there are not enough seats left.", ok: "Thank you for registering!", okText: "You will shortly receive a confirmation by email.", notFound: "Event not found.", formH: "Registration", dur: "min." },
  );

  useEffect(() => {
    supabase.from("events").select("*").eq("slug", slug!).eq("published", true).maybeSingle().then(({ data }) => {
      setEv((data as unknown as EventRow) ?? null);
      if (data) supabase.rpc("event_seats_taken", { _event_id: data.id }).then(({ data: n }) => setTaken(Number(n) || 0));
    });
  }, [slug]);

  useSeo({
    title: ev ? `${evTitle(ev, lang)} | Veranstaltungen` : "Veranstaltung",
    description: ev ? evDesc(ev, lang).replace(/<[^>]+>/g, " ").slice(0, 155) : "",
    canonical: `/veranstaltungen/${slug}`,
    jsonLd: ev && ev.starts_at ? {
      "@context": "https://schema.org", "@type": "Event", name: ev.title, startDate: ev.starts_at,
      endDate: new Date(new Date(ev.starts_at!).getTime() + ev.duration_minutes * 60000).toISOString(),
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode", eventStatus: "https://schema.org/EventScheduled",
      location: { "@type": "Place", name: ev.location, address: ev.location },
      image: ev.cover_image_url ?? undefined,
      offers: { "@type": "Offer", price: Number(ev.price_eur), priceCurrency: "EUR", url: absoluteUrl(`/veranstaltungen/${ev.slug}`) },
      organizer: { "@type": "Person", name: "Isabella Bartak" },
    } : undefined,
    jsonLdId: "event",
  });

  if (ev === undefined) return <div className="min-h-screen bg-background"><Header /></div>;

  const free = ev ? Math.max(0, ev.max_participants - taken) : 0;
  const isPast = ev?.starts_at ? new Date(ev.starts_at) <= new Date() : false;
  const announce = ev ? isAnnouncement(ev) : false;
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    if (!schema.safeParse(form).success || !cPriv || !cBind) return setErr(t.invalid);
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("register-event", {
      body: { ...form, eventId: ev!.id, persons, consentPrivacy: cPriv, consentBinding: cBind, lang },
    });
    setBusy(false);
    if (error || !data?.success) {
      let code = "";
      try { code = (await (error as any)?.context?.json())?.error; } catch { /* ignore */ }
      return setErr(code === "event_full" ? t.fullErr : t.fail);
    }
    setDone(true);
    setTaken((n) => n + persons);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <Link to={lp("/veranstaltungen")} className="text-sm text-primary hover:underline">{t.back}</Link>
          {!ev ? <p className="py-12 text-muted-foreground">{t.notFound}</p> : (
            <div className="grid gap-10 lg:grid-cols-[1fr_380px] mt-6">
              <article>
                {ev.cover_image_url && <img src={ev.cover_image_url} alt="" className="w-full aspect-video object-cover rounded-2xl mb-6" />}
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-6">{evTitle(ev, lang)}</h1>
                <SpeakerList speakers={ev.speakers ?? []} />
                <div className="prose prose-lg max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: evDesc(ev, lang).includes("<") ? evDesc(ev, lang) : evDesc(ev, lang).split(/\n{2,}/).map((p) => `<p>${p.replace(/[<>&]/g, "")}</p>`).join("") }} />
              </article>
              <aside className="space-y-6">
                <div className="rounded-2xl border border-border bg-card p-6 space-y-2 text-sm">
                  {ev.starts_at ? (<>
                    <p className="flex gap-2"><Calendar className="w-4 h-4 text-primary shrink-0" />{fmtDate(ev.starts_at, lang)}</p>
                    <p className="flex gap-2"><Clock className="w-4 h-4 text-primary shrink-0" />{fmtTime(ev.starts_at, lang)} · ca. {ev.duration_minutes} {t.dur}</p>
                  </>) : (
                    <p className="flex gap-2"><Calendar className="w-4 h-4 text-primary shrink-0" />{evDateNote(ev, lang)}</p>
                  )}
                  {ev.location && <p className="flex gap-2"><MapPin className="w-4 h-4 text-primary shrink-0" />{ev.location}</p>}
                  <p className="flex gap-2"><Euro className="w-4 h-4 text-primary shrink-0" />{fmtPrice(ev.price_eur, lang)}</p>
                  {!announce && <p className="flex gap-2"><Users className="w-4 h-4 text-primary shrink-0" />{free} {t.seats}</p>}
                  {(ev.catering ?? []).length > 0 && (
                    <p className="flex gap-2"><UtensilsCrossed className="w-4 h-4 text-primary shrink-0" />
                      <span>{lang === "en" ? "Refreshments: " : "Verpflegung: "}{(ev.catering ?? []).map((k) => cateringLabel(k, lang)).join(", ")}</span>
                    </p>
                  )}
                  {ev.qa && (
                    <p className="flex gap-2"><MessageCircleQuestion className="w-4 h-4 text-primary shrink-0" />
                      <span>{lang === "en" ? "Personal Q&A session: your questions are expressly welcome." : "Persönliche Fragerunde: Ihre Fragen sind ausdrücklich willkommen."}</span>
                    </p>
                  )}
                </div>
                <div id="anmeldung" className="rounded-2xl border border-border bg-card p-6">
                  {isPast ? <p className="text-muted-foreground">{t.past}</p> : announce ? (
                    <div className="space-y-2">
                      <h2 className="font-semibold text-lg">{lang === "en" ? "Coming soon" : "Demnächst"}</h2>
                      <p className="text-sm text-muted-foreground">{lang === "en"
                        ? "Registration is not open yet. Details and registration will follow here soon. Questions: office@ibpraxis.net"
                        : "Die Anmeldung ist noch nicht geöffnet. Details und Anmeldung folgen in Kürze an dieser Stelle. Fragen gerne an office@ibpraxis.net"}</p>
                    </div>
                  ) : done ? (
                    <div className="text-center py-4">
                      <CheckCircle2 className="w-10 h-10 text-primary mx-auto mb-3" />
                      <p className="font-semibold">{t.ok}</p>
                      <p className="text-sm text-muted-foreground mt-1">{t.okText}</p>
                    </div>
                  ) : free === 0 ? (
                    <Button disabled className="w-full">{t.full}</Button>
                  ) : (
                    <form onSubmit={submit} className="space-y-3">
                      <h2 className="font-semibold text-lg">{t.formH}</h2>
                      <Input placeholder={t.fn} value={form.firstName} onChange={set("firstName")} maxLength={100} required />
                      <Input placeholder={t.ln} value={form.lastName} onChange={set("lastName")} maxLength={100} required />
                      <Input type="email" placeholder={t.em} value={form.email} onChange={set("email")} maxLength={255} required />
                      <Input placeholder={t.ph} value={form.phone} onChange={set("phone")} maxLength={50} />
                      <label className="flex items-center justify-between text-sm">{t.pe}
                        <select value={persons} onChange={(e) => setPersons(Number(e.target.value))} className="border border-input rounded-md bg-background px-2 py-1">
                          {Array.from({ length: Math.min(5, free) }, (_, i) => i + 1).map((n) => <option key={n}>{n}</option>)}
                        </select>
                      </label>
                      <Textarea placeholder={t.no} value={form.note} onChange={set("note")} maxLength={2000} rows={3} />
                      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
                      <label className="flex gap-2 text-xs text-muted-foreground items-start">
                        <Checkbox checked={cPriv} onCheckedChange={(v) => setCPriv(v === true)} className="mt-0.5" />
                        <span>{t.priv1}<span className="font-medium text-foreground">{t.privLink}</span>{t.priv2}</span>
                      </label>
                      <label className="flex gap-2 text-xs text-muted-foreground items-start">
                        <Checkbox checked={cBind} onCheckedChange={(v) => setCBind(v === true)} className="mt-0.5" />
                        <span>{t.bind(fmtPrice(ev.price_eur, lang))}</span>
                      </label>
                      <p className="text-xs text-muted-foreground">{t.pay}</p>
                      {err && <p className="text-sm text-destructive">{err}</p>}
                      <Button type="submit" className="w-full" disabled={busy}>{busy ? "…" : t.reg}</Button>
                    </form>
                  )}
                </div>
              </aside>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VeranstaltungDetail;
